import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import "./index.css";
import App from "./App.tsx";
import { getEnvironment } from "./shared/hooks/useGetEnvironment";

import posthog from "posthog-js";
import { PostHogProvider } from "@posthog/react";
import * as Sentry from "@sentry/react";

const environment = getEnvironment();
const isProduction = environment === "prod";

const sentryDsn = import.meta.env.VITE_PUBLIC_SENTRY_DSN;

if (sentryDsn && isProduction) {
  Sentry.init({
    dsn: sentryDsn,
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    // Enable logs to be sent to Sentry
    enableLogs: true,
    environment: environment,
    tracesSampleRate: 1.0,
  });
}

// Initialize PostHog ONLY in production
// @elayne.com users are excluded from session recording
const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY;
const posthogHost = "https://t.elayne.com";
const uiHost = "https://us.posthog.com";

if (posthogKey && isProduction) {
  posthog.init(posthogKey, {
    api_host: posthogHost,
    ui_host: uiHost,
    session_recording: {
      maskAllInputs: false,
      maskTextSelector: ".sensitive, [data-sensitive]",
      blockSelector: ".no-record, [data-no-record]",
      inlineStylesheet: true,
    },
    autocapture: true,
    capture_pageview: "history_change",
    capture_pageleave: true,
    // Supabase OAuth redirects put access/refresh/provider tokens in the URL hash
    disable_capture_url_hashes: true,
    loaded: (posthog) => {
      posthog.register({
        environment: environment,
      });

      console.log(`PostHog loaded (production)`);

      // Wait for PostHog /decide endpoint to return before checking recording status
      setTimeout(() => {
        const isRecording = posthog.sessionRecordingStarted?.() ?? false;

        console.log("Session recording status:", isRecording);
        console.log("Session ID:", posthog.get_session_id());

        if (isRecording) {
          console.log("Replay URL:", posthog.get_session_replay_url());
          console.log("Tip: Interact for 30s, then check PostHog in ~2min");
        } else {
          console.warn("Session recording NOT started. Troubleshoot:");
          console.warn("  1. PostHog → Settings → Session Replay → Enable it");
          console.warn(
            "  2. Check Network tab → /decide endpoint → look for sessionRecording: true",
          );
          console.warn("  3. Verify your plan includes session replay");
        }
      }, 2000);
    },
  });
} else {
  // PostHog not initialized - either missing key or not production
  if (!posthogKey) {
    console.warn("PostHog not initialized - VITE_PUBLIC_POSTHOG_KEY not found");
  } else if (!isProduction) {
    console.log(`PostHog disabled (${environment} environment - production only)`);
  }
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <PostHogProvider client={posthog}>
      <App />
    </PostHogProvider>
  </StrictMode>,
);
